/**
 * Network Logger Service
 *
 * Intercepts fetch and XMLHttpRequest to capture network requests
 * for inclusion in bug reports. Preserves original behavior.
 */

export interface NetworkRequestEntry {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  status?: number;
  statusText?: string;
  duration?: number;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  error?: string;
  type: 'fetch' | 'xhr';
}

const MAX_ENTRIES = 100;

class NetworkLoggerService {
  private entries: NetworkRequestEntry[] = [];
  private initialized = false;
  private requestId = 0;
  private originalFetch: typeof fetch | null = null;

  /**
   * Initialize the network logger by intercepting fetch and XHR.
   * Should be called once at app startup.
   */
  initialize(): void {
    if (this.initialized) return;

    this.interceptFetch();
    this.interceptXHR();

    this.initialized = true;
  }

  private generateId(): string {
    return `req-${++this.requestId}-${Date.now()}`;
  }

  private interceptFetch(): void {
    this.originalFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const id = this.generateId();
      const startTime = Date.now();
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
      const method = init?.method || 'GET';

      // Capture request headers (sanitize auth tokens)
      const requestHeaders: Record<string, string> = {};
      if (init?.headers) {
        const headers = new Headers(init.headers);
        headers.forEach((value, key) => {
          requestHeaders[key] = key.toLowerCase() === 'authorization' ? '[REDACTED]' : value;
        });
      }

      const entry: NetworkRequestEntry = {
        id,
        timestamp: new Date().toISOString(),
        method: method.toUpperCase(),
        url: this.sanitizeUrl(url),
        requestHeaders,
        type: 'fetch',
      };

      try {
        const response = await this.originalFetch!(input, init);

        // Capture response info
        entry.status = response.status;
        entry.statusText = response.statusText;
        entry.duration = Date.now() - startTime;

        // Capture response headers
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          responseHeaders[key] = value;
        });
        entry.responseHeaders = responseHeaders;

        this.addEntry(entry);
        return response;
      } catch (error) {
        entry.duration = Date.now() - startTime;
        entry.error = error instanceof Error ? error.message : String(error);
        this.addEntry(entry);
        throw error;
      }
    };
  }

  private interceptXHR(): void {
    const self = this;
    const OriginalXHR = window.XMLHttpRequest;

    // Create a new XMLHttpRequest constructor
    function InterceptedXHR(this: XMLHttpRequest) {
      const xhr = new OriginalXHR();
      const id = self.generateId();
      let method = 'GET';
      let url = '';
      let startTime = 0;
      const requestHeaders: Record<string, string> = {};

      // Intercept open
      const originalOpen = xhr.open.bind(xhr);
      xhr.open = (
        _method: string,
        _url: string | URL,
        async: boolean = true,
        username?: string | null,
        password?: string | null
      ) => {
        method = _method.toUpperCase();
        url = typeof _url === 'string' ? _url : _url.toString();
        startTime = Date.now();
        return originalOpen(_method, _url, async, username, password);
      };

      // Intercept setRequestHeader
      const originalSetHeader = xhr.setRequestHeader.bind(xhr);
      xhr.setRequestHeader = (name: string, value: string) => {
        requestHeaders[name] = name.toLowerCase() === 'authorization' ? '[REDACTED]' : value;
        return originalSetHeader(name, value);
      };

      // Intercept send
      const originalSend = xhr.send.bind(xhr);
      xhr.send = (body?: Document | XMLHttpRequestBodyInit | null) => {
        startTime = Date.now();

        xhr.addEventListener('loadend', () => {
          const entry: NetworkRequestEntry = {
            id,
            timestamp: new Date().toISOString(),
            method,
            url: self.sanitizeUrl(url),
            status: xhr.status,
            statusText: xhr.statusText,
            duration: Date.now() - startTime,
            requestHeaders,
            type: 'xhr',
          };

          // Try to parse response headers
          const headerStr = xhr.getAllResponseHeaders();
          if (headerStr) {
            const responseHeaders: Record<string, string> = {};
            headerStr.split('\r\n').forEach((line) => {
              // Split on first colon only to handle values containing colons (timestamps, URLs)
              const idx = line.indexOf(': ');
              if (idx > -1) {
                const name = line.substring(0, idx);
                const value = line.substring(idx + 2);
                responseHeaders[name] = value;
              }
            });
            entry.responseHeaders = responseHeaders;
          }

          if (xhr.status === 0) {
            entry.error = 'Network error or request aborted';
          }

          self.addEntry(entry);
        });

        return originalSend(body);
      };

      return xhr;
    }

    // Copy static properties
    InterceptedXHR.prototype = OriginalXHR.prototype;
    Object.defineProperty(InterceptedXHR, 'UNSENT', { value: 0 });
    Object.defineProperty(InterceptedXHR, 'OPENED', { value: 1 });
    Object.defineProperty(InterceptedXHR, 'HEADERS_RECEIVED', { value: 2 });
    Object.defineProperty(InterceptedXHR, 'LOADING', { value: 3 });
    Object.defineProperty(InterceptedXHR, 'DONE', { value: 4 });

    // Replace global XHR
    (window as unknown as { XMLHttpRequest: typeof XMLHttpRequest }).XMLHttpRequest =
      InterceptedXHR as unknown as typeof XMLHttpRequest;
  }

  private sanitizeUrl(url: string): string {
    // Remove sensitive query params
    try {
      const parsed = new URL(url, window.location.origin);
      const sensitiveParams = ['token', 'key', 'secret', 'password', 'auth'];
      sensitiveParams.forEach((param) => {
        if (parsed.searchParams.has(param)) {
          parsed.searchParams.set(param, '[REDACTED]');
        }
      });
      return parsed.toString();
    } catch {
      return url;
    }
  }

  private addEntry(entry: NetworkRequestEntry): void {
    this.entries.push(entry);

    // Trim to max entries
    if (this.entries.length > MAX_ENTRIES) {
      this.entries = this.entries.slice(-MAX_ENTRIES);
    }
  }

  /**
   * Get all captured network requests
   */
  getEntries(): NetworkRequestEntry[] {
    return [...this.entries];
  }

  /**
   * Get failed requests (4xx, 5xx, or errors)
   */
  getFailedRequests(): NetworkRequestEntry[] {
    return this.entries.filter((entry) => entry.error || (entry.status && entry.status >= 400));
  }

  /**
   * Clear all captured entries
   */
  clear(): void {
    this.entries = [];
  }

  /**
   * Get summary statistics
   */
  getStats(): {
    total: number;
    failed: number;
    avgDuration: number;
  } {
    const failed = this.entries.filter((e) => e.error || (e.status && e.status >= 400)).length;
    const durations = this.entries.filter((e) => e.duration).map((e) => e.duration!);
    const avgDuration = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;

    return {
      total: this.entries.length,
      failed,
      avgDuration: Math.round(avgDuration),
    };
  }
}

// Singleton instance
export const networkLogger = new NetworkLoggerService();
